/**
 * Endpoint mapping — central reference for all FastAPI endpoint paths.
 * Components should import paths from here instead of hard-coding strings.
 */

// ─── Authentication ─────────────────────────────────────────────────────────
export const AUTH = {
  LOGIN: "/api/v1/auth/login",
  PARTNER_LOGIN: "/api/v1/auth/partner/login",
  LOGOUT: "/api/v1/auth/logout",
  REFRESH: "/api/v1/auth/refresh",
  CHANGE_PASSWORD: "/api/v1/auth/change-password",
  FORGOT_PASSWORD: "/api/v1/users/forgot-password",
  RESET_PASSWORD: "/api/v1/users/reset-password",
  SEND_OTP: "/api/v1/users/otp/send",
  VERIFY_OTP: "/api/v1/users/otp/verify",
  SIGN_UP: "/api/v1/users/signup",
};

// ─── Access / generic filter surface ────────────────────────────────────────
//
// Single source of truth for filter, restriction, and access-related list
// calls. Use these instead of hitting /users, /admin/users, or
// /partner-admin/users directly when you need a filtered roster for a
// dropdown / picker / restriction widget. Role-based scope is handled
// server-side (own org / partner / cross-tenant) — same URL for every
// caller.
//
// Query params:
//   ?portal=income_tax|gst|tds  — effective access (user flag AND org flag)
//   ?assignable_only=true       — role ∈ {user, tenant_admin}, enabled+activated
//   ?search=<str>               — ILIKE on email / full_name / first/last name
//   ?page=, ?page_size=         — standard pagination (default page_size=50)
//
// Response: { items[], total, page, page_size, total_pages }.
export const ACCESS = {
  USERS: "/api/v1/access/users",
};

// ─── User & Dashboard ───────────────────────────────────────────────────────
export const USERS = {
  ME: "/api/v1/users/me",
  USER_INFO: "/api/v1/users/me",
  // Team management (tenant scope, NOT /admin/* — those are super_admin only).
  // GET /users supports `search` typeahead + paginated page/page_size response.
  // POST /users/invite, PUT /users/{id}/role, POST /users/resend-invite/{id},
  // DELETE /users/{id} all admin-gated server-side. PUT /users/{id} +
  // PUT /users/{id}/toggle-status existed already; we just call them by URL.
  LIST: "/api/v1/users",
  INVITE: "/api/v1/users/invite",
  RESEND_INVITE: (id) => `/api/v1/users/resend-invite/${id}`,
  CHANGE_ROLE: (id) => `/api/v1/users/${id}/role`,
  TOGGLE_STATUS: (id) => `/api/v1/users/${id}/toggle-status`,
  UPDATE: (id) => `/api/v1/users/${id}`,
  DELETE: (id) => `/api/v1/users/${id}`,
};

export const DASHBOARD = {
  INCOME_TAX_CARDS: "/api/v1/income-tax/dashboard/cards",
  GST_CARDS: "/api/v1/gst/dashboard/cards",
  TDS_CARDS: "/api/v1/tds/dashboard/cards",
  CALENDAR_DUES: "/api/v1/income-tax/calendar/dues",
  // GST/TDS use existing portal-specific endpoints (different param +
  // response shape from IT — Calender.jsx branches on portal prop).
  CALENDAR_DUES_GST: "/api/v1/gst/calendar",
  CALENDAR_DUES_TDS: "/api/v1/tds/calendar",
};

// ─── Clients ─────────────────────────────────────────────────────────────────
export const CLIENTS = {
  INCOME_TAX_LIST: "/api/v1/clients/income-tax",
  GST_LIST: "/api/v1/clients/gst",
  TDS_LIST: "/api/v1/clients/tds",
  CREATE_INCOME_TAX: "/api/v1/clients/income-tax",
  CREATE_GST: "/api/v1/clients/gst",
  CREATE_TDS: "/api/v1/clients/tds",
  INCOME_TAX_DETAILS: "/api/v1/clients/income-tax/details",
  GST_DETAILS: "/api/v1/clients/gst/details",
  TDS_DETAILS: "/api/v1/clients/tds/details",
  IMPORT: "/api/v1/clients/import",
  DOWNLOAD_IMPORT_TEMPLATE: "/api/v1/clients/import/template",
  CHECK_IMPORT_STATUS: "/api/v1/clients/import/status",
  LAST_DATA_IMPORT: "/api/v1/clients/import/last",
  DELETE: "/api/v1/clients/delete",

  // User-level client assignment (USER_LEVEL_CLIENT_ASSIGNMENT.md §5.4-5.5).
  // `portal` is the URL segment (`income-tax` | `gst` | `tds`). Single-record
  // transfer body: { user_id: <uuid> | null }; bulk transfer body:
  // { client_ids: [...], user_id: <uuid> | null }. BE accepts `null` AND the
  // string `"unassigned"` interchangeably for the Shared pool — use `null`
  // for symmetry with create/update calls.
  ASSIGN: (portal, clientId) => `/api/v1/clients/${portal}/${clientId}/assign`,
  BULK_ASSIGN: (portal) => `/api/v1/clients/${portal}/bulk-assign`,
};

// ─── Notice Lists ────────────────────────────────────────────────────────────
export const NOTICES = {
  INCOME_TAX_LIST: "/api/v1/income-tax/notices",
  GST_LIST: "/api/v1/gst/notices",
  TDS_LIST: "/api/v1/tds/notices",
  GST_ADDITIONAL_LIST: "/api/v1/gst/additional-notices",
  GST_ADDITIONAL_DETAILS: "/api/v1/gst/additional-notices/details",
  UNIQUE_SECTIONS: "/api/v1/income-tax/notice-sections",
  // Manual Status (Open | Close) persist for a GST notice / additional-notice.
  GST_UPDATE_NOTICE_STATUS: "/api/v1/gst/update-notice-status",
  // Bulk manual Status (Open | Close) for the notices ticked on ANY portal's
  // list page. One endpoint for all five doctypes; payload mirrors the
  // portal delete-documents calls: { doctype, docnames, notice_status }.
  BULK_UPDATE_STATUS: "/api/v1/notices/update-status",
};

// ─── Document Operations (generic CRUD) ──────────────────────────────────────
export const DOCUMENTS = {
  GET_DATA: "/api/v1/documents/data",
  SAVE: "/api/v1/documents/save",
  SAVE_DATA: "/api/v1/documents/save-data",
  DELETE: "/api/v1/documents/delete",
  FAST_DELETE: "/api/v1/documents/fast-delete",
  // Portal-specific fast-delete endpoints — the legacy FAST_DELETE above
  // returns 404 on FastAPI (generic `/documents/fast-delete` was never
  // ported). Each portal now has its own `/delete-documents` route.
  DELETE_NOTICES_INCOME_TAX: "/api/v1/income-tax/delete-documents",
  DELETE_NOTICES_GST: "/api/v1/gst/delete-documents",
  DELETE_NOTICES_TDS: "/api/v1/tds/delete-documents",
  // Client-scoped variant: deletes EVERY notice document of the given
  // client(s) in one call (both IT doctypes + child rows + Case Details chain
  // + S3), instead of enumerating notice ids. Payload: {client_names: [id,...]}.
  // Used by the Total Notices client picker's "Delete All Notices".
  DELETE_CLIENT_NOTICES_INCOME_TAX: "/api/v1/income-tax/delete-client-documents",
  GET_COUNT: "/api/v1/documents/count",
  GET_LIST: "/api/v1/documents/list",
  UPLOAD_FILE: "/api/v1/documents/upload",
  DOWNLOAD_FILE: "/api/v1/documents/download",
};

// ─── AI Response & Summary ───────────────────────────────────────────────────
export const AI = {
  GENERATE_RESPONSE: "/api/v1/ai/generate-response",
  // Async-polling sibling for `GENERATE_RESPONSE` — backend returns
  // 202 + task_id, FE polls this until {status: done|failed}.
  GENERATE_RESPONSE_STATUS: (taskId) =>
    `/api/v1/ai/generate-response/status/${taskId}`,
  VIEW_MASKED_DATA: "/api/v1/ai/masked-data/view",
  IS_RESPONSE_IN_PROGRESS: "/api/v1/ai/response-in-progress",
  FINALIZE_RESPONSE: "/api/v1/ai/response/finalize",
  UPDATE_RESPONSE_STATUS: "/api/v1/ai/response/status",
  GENERATE_SUMMARY: "/api/v1/ai/summary/generate",
  // Case fragments — portal-specific (POST with {voucher_type, voucher_no} body).
  // Old `/api/v1/ai/case-fragments` URL was wrong (404); these route to the
  // portal-namespaced endpoints that match the rest of the API design.
  GET_CASE_FRAGMENTS_IT: "/api/v1/income-tax/case-fragments",
  GET_CASE_FRAGMENTS_GST: "/api/v1/gst/case-fragments",
  DOWNLOAD_CASE_FRAGMENTS_DOCX_IT: "/api/v1/income-tax/case-fragments/docx",
  DOWNLOAD_CASE_FRAGMENTS_DOCX_GST: "/api/v1/gst/case-fragments/docx",
  GENERATE_PDF: "/api/v1/ai/generate-pdf",
};

// ─── Requirements Checklist (documents needed to answer a notice) ────────────
export const CHECKLIST = {
  GENERATE: "/api/v1/checklist/generate",
  BY_NOTICE: (noticeType, noticeId) =>
    `/api/v1/checklist/${encodeURIComponent(noticeType)}/${encodeURIComponent(noticeId)}`,
  UPDATE_ITEM: (checklistId, itemId) =>
    `/api/v1/checklist/${checklistId}/item/${encodeURIComponent(itemId)}`,
  EXPORT: (checklistId, format) =>
    `/api/v1/checklist/${checklistId}/export?format=${format}`,
  SHARE: (checklistId) => `/api/v1/checklist/${checklistId}/share`,
  SHARE_STATUS: (checklistId) => `/api/v1/checklist/${checklistId}/share/status`,
  // Send the minted link to the subscriber through the approved WhatsApp
  // template (System Configuration > whatsapp_template_name).
  SHARE_WHATSAPP: (checklistId) => `/api/v1/checklist/${checklistId}/share/whatsapp`,
};

// ─── Public checklist upload (no-login, tokenised) ───────────────────────────
export const PUBLIC_CHECKLIST = {
  GET: (token) => `/api/v1/public/checklist/${encodeURIComponent(token)}`,
  UPLOAD: (token) => `/api/v1/public/checklist/${encodeURIComponent(token)}/upload`,
};

// ─── Review Mode (source-tagged response verification) ───────────────────────
// The clean draft stays exactly what a normal generation produces; these serve
// the additional tagged copy the reviewer confirms against.
export const REVIEW = {
  BUILD: (noticeType, noticeId) =>
    `/api/v1/response/review/build/${encodeURIComponent(noticeType)}/${encodeURIComponent(noticeId)}`,
  BY_NOTICE: (noticeType, noticeId) =>
    `/api/v1/response/review/by-notice/${encodeURIComponent(noticeType)}/${encodeURIComponent(noticeId)}`,
  SESSION: (sessionId) => `/api/v1/response/review/${sessionId}`,
  CONFIRM_TAG: (sessionId, tagId) =>
    `/api/v1/response/review/${sessionId}/tag/${tagId}`,
  FINALISE: (sessionId) => `/api/v1/response/review/${sessionId}/finalise`,
  // Backs both "Continue Without Review" and "Cancel Review": one decision.
  SKIP: (sessionId) => `/api/v1/response/review/${sessionId}/skip`,
  REOPEN: (sessionId) => `/api/v1/response/review/${sessionId}/reopen`,
  ADD_POINT: (sessionId) => `/api/v1/response/review/${sessionId}/add-point`,
  HEARTBEAT: (sessionId) => `/api/v1/response/review/${sessionId}/heartbeat`,
};

// ─── Managed Service (Review User work queue) ────────────────────────────────
// The Review Dashboard consumes DASHBOARD (org-grouped, notice-enriched);
// the workflow actions mirror backend managed_service.py.
export const MANAGED_SERVICE = {
  REQUEST: `/api/v1/managed-service/request`,
  MY_REQUEST: (noticeType, noticeId) =>
    `/api/v1/managed-service/request/${encodeURIComponent(noticeType)}/${encodeURIComponent(noticeId)}`,
  REVIEWER_QUEUE: `/api/v1/managed-service/reviewer/queue`,
  REVIEWER_DASHBOARD: `/api/v1/managed-service/reviewer/dashboard`,
  // Analytics for the reviewer landing page (tables live on the portal pages).
  REVIEWER_STATS: `/api/v1/managed-service/reviewer/stats`,
  CLAIM: (requestId) => `/api/v1/managed-service/reviewer/${requestId}/claim`,
  ACCEPT: (requestId) => `/api/v1/managed-service/reviewer/${requestId}/accept`,
  REJECT: (requestId) => `/api/v1/managed-service/reviewer/${requestId}/reject`,
  // Reviewer drafting: generate the response in Review Mode, then deliver it.
  DRAFT: (requestId) => `/api/v1/managed-service/reviewer/${requestId}/draft`,
  // Async sibling of DRAFT: the final submit carrying the chosen legal issues
  // runs in a worker, so the page polls this for the stage and the result.
  DRAFT_STATUS: (requestId, taskId) =>
    `/api/v1/managed-service/reviewer/${requestId}/draft/status/${taskId}`,
  DELIVER: (requestId) => `/api/v1/managed-service/reviewer/${requestId}/deliver`,
  WORKSPACE: (requestId) =>
    `/api/v1/managed-service/reviewer/${requestId}/workspace`,
  REVIEWER_GET: (requestId) => `/api/v1/managed-service/reviewer/${requestId}`,
  // Reviewer routing (04-10-2026): assigned -> accepted, disown back to open,
  // capture an open one, and the admin-only reassign + its reviewer picker.
  CAPTURE: (requestId) => `/api/v1/managed-service/reviewer/${requestId}/capture`,
  DISOWN: (requestId) => `/api/v1/managed-service/reviewer/${requestId}/disown`,
  REASSIGN: (requestId) => `/api/v1/managed-service/reviewer/${requestId}/reassign`,
  REVIEWERS: (portal) =>
    `/api/v1/managed-service/reviewer/reviewers${portal ? `?portal=${encodeURIComponent(portal)}` : ""}`,
};

// ─── Lambda / Parallel Fetching ──────────────────────────────────────────────
export const LAMBDA = {
  // NOTE: Repointed from the orphan /webhooks/lambda/trigger-parallel-fetch
  // (never implemented in backend) to the real batch endpoint. Callers now
  // must POST `{client_names: [<id>, ...]}` instead of `{portal_type: "..."}`.
  TRIGGER_PARALLEL_FETCH: "/api/v1/income-tax/fetch-notices/batch",
  // Single-client IT fetch — used by ClientView page. Backend reads `client`
  // (string) from payload; emits `scope: "single"` audit + hard 409 on concurrent.
  IT_FETCH_NOTICES_SINGLE: "/api/v1/income-tax/fetch-notices",
  PROCESS_SELECTED_CLIENTS: "/api/v1/webhooks/lambda/process-selected-clients",
  // Real FastAPI route — prior /webhooks/lambda/fetch-status never existed
  // as an endpoint (404), which blocked the batch-progress banner.
  GET_FETCH_STATUS: "/api/v1/income-tax/fetch-status",
  FETCH_FAILED_LOGIN: "/api/v1/income-tax/fetch-failed-login",
  // R6 — GST portal copy. IT endpoint above explicitly 400s on
  // payload.portal != "Income Tax", so GST callers need their own URL.
  FETCH_FAILED_LOGIN_GST: "/api/v1/gst/fetch-failed-login",
  // GST batch trigger — equivalent to TRIGGER_PARALLEL_FETCH above but
  // for GST. Was previously routed through PROCESS_SELECTED_CLIENTS
  // (`/webhooks/lambda/process-selected-clients`) which is the older
  // shared per-portal route. The new dedicated `/gst/fetch-notices/batch`
  // endpoint matches the IT shape so the syncNotices mapping in
  // Clients.jsx is symmetric.
  TRIGGER_PARALLEL_FETCH_GST: "/api/v1/gst/fetch-notices/batch",
  // Single-client GST fetch — used by ClientView page. Same shape as
  // IT_FETCH_NOTICES_SINGLE.
  GST_FETCH_NOTICES_SINGLE: "/api/v1/gst/fetch-notices",
  // GST batch progress poll — mirrors GET_FETCH_STATUS for IT.
  GET_FETCH_STATUS_GST: "/api/v1/gst/fetch-status",
  // Realtime fetch-progress stream (SSE / EventSource). Resolves the run by
  // log id and is portal-agnostic (an IT log streams here too), mirroring how
  // GET_FETCH_STATUS is reused cross-portal. The banner opens this first and
  // falls back to the 10s poll above if the stream errors. Usage:
  //   `${base}/api/v1/gst/fetch-events/${logId}?token=${authToken}`
  FETCH_EVENTS: "/api/v1/gst/fetch-events", // + `/${logId}?token=`
  // Super-admin only — detailed per-notice fetch timing for a run.
  GET_FETCH_TIMING_GST: "/api/v1/gst/fetch-timing", // + `/${logId}`

  // Whitebooks per-GSTIN auth surface — tenant-scoped routes that
  // power the Whitebooks Session card on the GST Client detail page
  // and the fetch-time inline OTP retry modal. Endpoints look up the
  // GSTClient by id + verify tenant ownership server-side, then
  // derive gst_username + state_cd from THAT row — clients never
  // pass a gst_username themselves (prevents cross-tenant spoofing).
  GST_CLIENT_WHITEBOOKS_STATUS:    (id) => `/api/v1/gst/clients/${id}/whitebooks/status`,
  GST_CLIENT_WHITEBOOKS_START_OTP: (id) => `/api/v1/gst/clients/${id}/whitebooks/start-otp`,
  GST_CLIENT_WHITEBOOKS_SUBMIT_OTP:(id) => `/api/v1/gst/clients/${id}/whitebooks/submit-otp`,
  GST_CLIENT_WHITEBOOKS_LOGOUT:    (id) => `/api/v1/gst/clients/${id}/whitebooks/logout`,
};

// ─── Portal Login / Events ───────────────────────────────────────────────────
export const EVENTS = {
  INCOME_TAX_LOGIN: "/api/v1/webhooks/portal/income-tax/login",
  GST_LOGIN: "/api/v1/webhooks/portal/gst/login",
  TDS_LOGIN: "/api/v1/webhooks/portal/tds/login",
  TDS_PROCESS_ALL: "/api/v1/tds/process-all-clients",
  TDS_PROCESS_SELECTED: "/api/v1/tds/process-selected-clients",
  TDS_REQUEST_JR: "/api/v1/tds/request-justification-report",
  TDS_UNZIP: "/api/v1/tds/unzip-file",
  TDS_UTILITY: "/api/v1/tds/utility",
};

// ─── Previous Context (response flow) ────────────────────────────────────────
// Load-Previous-Context reuses the GST Appeal Drafting machinery: the doc list
// mirrors the appeal case-documents payload (grouped Orders/Notices/Replies),
// selections persist as AttachmentItem rows and feed the shared
// build_selected_docs_bundle service at response time.
export const CONTEXT = {
  // Selectable case documents for a GST additional notice (un-gated).
  // NOTE: distinct path (not /previous-context/documents/...) so it can't be
  // shadowed by the existing GET /previous-context/{notice_type}/{record_id}
  // route (FastAPI matches in declaration order — a static "documents" segment
  // under that dynamic route would be captured as notice_type="documents").
  GET_DOCUMENTS: (additionalNoticeId) =>
    `/api/v1/gst/previous-context-documents/${additionalNoticeId}`,
  // Persist the user's selection for this notice.
  SAVE_SELECTIONS: "/api/v1/gst/previous-context-save",

  // Income Tax variants (2026-07-11). Same feature, IT-scoped: `kind` is
  // "eproceeding" (E-Proceeding) or "response_outstanding" (Response to
  // Outstanding Demand). The AI response flow consumes the saved selection
  // portal-agnostically (keyed on the record id), so only these list/save
  // endpoints differ from GST.
  IT_GET_DOCUMENTS: (recordId, kind) =>
    `/api/v1/income-tax/previous-context-documents/${recordId}?kind=${kind}`,
  IT_SAVE_SELECTIONS: "/api/v1/income-tax/previous-context-save",
};

// ─── Data Import ─────────────────────────────────────────────────────────────
export const DATA_IMPORT = {
  START: "/api/v1/clients/import/start",
  STATUS: "/api/v1/clients/import/status",
  PREVIEW: "/api/v1/clients/import/preview",
  DOWNLOAD_ERRORED: "/api/v1/clients/import/download-errors",
  GET_DOC: "/api/v1/clients/import/document",
  ERRORS_AND_WARNINGS: "/api/v1/clients/import/errors-warnings",
};

// ─── Pricing ────────────────────────────────────────────────────────────────
export const PRICING = {
  GET_DETAILS: "/api/v1/subscriptions/pricing-details",
  GET_PAYMENT_DETAILS: "/api/v1/subscriptions/payment-details",
  SUBMIT_BILLING: "/api/v1/payments/submit-billing",
};

// ─── Payments (Razorpay Standard Checkout — Slice 4 of True Pricing) ─────────
export const PAYMENTS = {
  // POST — body: { plan_code, selected_portals[], cadence, promo_code?,
  //   additional_clients?, additional_responses? }
  // Returns: { order_id, razorpay_order_id, amount_paise, amount_inr,
  //   currency, key_id, plan_code, plan_name }
  // The BE recomputes the amount itself; any FE-supplied amount is ignored.
  RAZORPAY_CREATE_ORDER: "/api/v1/payments/razorpay/create-order",
  // Tenant org-level tax invoice details (first-time capture from the
  // Buy Now modal). 409 if already submitted — admin edits via
  // ADMIN.ORG_TAX_INVOICE_UPDATE.
  TAX_INVOICE_SUBMIT: "/api/v1/payments/tax-invoice",
  // POST — body: { order_id, razorpay_order_id, razorpay_payment_id,
  //   razorpay_signature }
  // Verifies the popup callback signature, marks order paid, activates
  // the plan, and flips has_chosen_subscription_path on the org.
  RAZORPAY_VERIFY: "/api/v1/payments/razorpay/verify",
  // POST — body: { order_id, reason? }
  // Called from the Razorpay popup's ondismiss hook so the BE Order
  // row stops looking ambiguous ("Created" forever) when the customer
  // closed the popup without paying. Idempotent — only flips orders
  // in 'created' state to 'cancelled'.
  RAZORPAY_CANCEL_ORDER: "/api/v1/payments/razorpay/cancel-order",
};

// ─── Subscriptions (Phase 4 features + Shop catalogue + pricing engine) ─────
export const SUBSCRIPTIONS = {
  // GET — current org's enabled-feature map. Drives FE conditional rendering.
  MY_FEATURES: "/api/v1/subscriptions/features",
  // GET — current org's plan status + trial / paid state + per-portal usage.
  STATUS: "/api/v1/subscriptions/status",
  // GET — all active plans grouped by category. Drives the Shop catalogue.
  PLANS: "/api/v1/subscriptions/plans",
  // POST — pricing engine: pass selected_portals + optional promo_code +
  // addons; receive base_total / discount / total / gst_tax breakdown.
  // Used by the Shop's Configure drawer when a user toggles portals or
  // applies a promo code.
  PRICING_DETAILS: "/api/v1/subscriptions/pricing-details",
  // POST — Pricing v2 engine (four-tier matrix). Body shape:
  //   { plan_code, selected_portals[], cadence, promo_code?,
  //     additional_clients?, additional_responses? }
  // Returns: { plan_code, plan_name, cadence, modules, breakdown:{…},
  //   promo:{applied_code,error}, total, currency }
  SHOP_PRICING: "/api/v1/subscriptions/shop-pricing",
  // GET — paginated user-facing order history for the caller's own org.
  // Powers the /usage page Order History section. Strictly scoped by
  // the BE to current_user.organization_id; admins still use the wider
  // /admin/payments/orders endpoint for cross-tenant views.
  ORDERS: "/api/v1/subscriptions/orders",
};

// ─── Onboarding (post-signup subscription path choice) ───────────────────────
export const ONBOARDING = {
  // GET — Returns {has_chosen_subscription_path, is_trial, trial_end_date,
  // trial_preview}. Router guard checks has_chosen_subscription_path to
  // decide whether to redirect to /onboarding/choose-path.
  STATUS: "/api/v1/onboarding/status",
  // POST — Starts the free trial for the caller's org. Idempotent (409
  // if path already chosen). Tenant admin only.
  START_TRIAL: "/api/v1/onboarding/start-trial",
};

// ─── Admin Portal (super_admin only) ────────────────────────────────────────
// Consumed by frontend pages under /admin/*. Backend endpoints land in phases;
// entries listed here in advance so pages can reference them without inlining.
export const ADMIN = {
  // Phase 1 — Organizations
  ORGS: "/api/v1/admin/organizations",
  ORG_CREATE: "/api/v1/admin/organizations",
  ORG_DETAIL: (id) => `/api/v1/admin/organizations/${id}`,
  ORG_UPDATE: (id) => `/api/v1/admin/organizations/${id}`,
  ORG_SUSPEND: (id) => `/api/v1/admin/organizations/${id}/suspend`,
  ORG_UNSUSPEND: (id) => `/api/v1/admin/organizations/${id}/unsuspend`,
  ORG_ACTIVITY: (id) => `/api/v1/admin/organizations/${id}/activity`,
  // Per-org composite usage rollup (revenue + AI + ops + usage + daily
  // series) consumed by the Usage tab on AdminOrganizationDetail.jsx.
  // Window selector flips ?window_days=7|30|90 (default 30 BE-side).
  ORG_USAGE_SUMMARY: (id) =>
    `/api/v1/admin/organizations/${id}/usage-summary`,
  // Soft-delete / restore / hard-purge lifecycle. Suspend stays separate
  // (suspend = login freeze, soft-delete = removed from default views,
  // purge = irreversible cascade). Purge supports `?dry_run=true` for the
  // impact-preview pass before the executing call.
  ORG_SOFT_DELETE: (id) => `/api/v1/admin/organizations/${id}/soft-delete`,
  ORG_RESTORE: (id) => `/api/v1/admin/organizations/${id}/restore`,
  ORG_PURGE: (id) => `/api/v1/admin/organizations/${id}/purge`,

  // Phase 1 — Users
  USERS: "/api/v1/admin/users",
  // Direct creation of an org-less Review User (nabs_reviewer) — the one
  // user shape the admin panel creates itself (tenant users arrive via
  // signup/invites instead).
  USERS_CREATE_REVIEWER: "/api/v1/admin/users/review-user",
  USER_DETAIL: (id) => `/api/v1/admin/users/${id}`,
  USER_UPDATE: (id) => `/api/v1/admin/users/${id}`,
  USER_FORCE_RESET: (id) => `/api/v1/admin/users/${id}/force-password-reset`,
  USER_SET_PASSWORD: (id) => `/api/v1/admin/users/${id}/set-password`,
  USER_DISABLE: (id) => `/api/v1/admin/users/${id}/disable`,
  USER_ENABLE: (id) => `/api/v1/admin/users/${id}/enable`,
  // Direct activation — bypasses the invite-email flow. Used when an
  // invited user can't access the email link (typo, lost inbox, etc.)
  // and admin wants to mark them ready-to-login. Flag-flip only;
  // pair with USER_SET_PASSWORD / USER_FORCE_RESET if a credential
  // change is also required.
  USER_ACTIVATE: (id) => `/api/v1/admin/users/${id}/activate`,
  // GDPR purge lives under the Phase 3 admin/gdpr module, not under /users.
  GDPR_PURGE: (userId) => `/api/v1/admin/gdpr/purge/${userId}`,

  // NOTE: `/admin/lookups/*` endpoints were scaffolded but never shipped
  // (404). Use `ADMIN.PARTNERS_LIST` and `ADMIN.PLANS` for Select
  // component data and filter the results client-side where needed.

  // Phase 2 — Subscriptions / quotas (route names match live backend)
  ORG_SUBSCRIPTION: (id) => `/api/v1/admin/organizations/${id}/subscription`,
  // Super-admin overwrites org tax-invoice fields after tenant has
  // submitted (the tenant endpoint locks on first submit).
  ORG_TAX_INVOICE_UPDATE: (id) =>
    `/api/v1/admin/organizations/${id}/tax-invoice`,
  // Reset is_tax_invoice_submitted=false so tenant can re-submit fresh
  // from /shop. The 6 data fields stay intact (modal opens pre-filled).
  ORG_TAX_INVOICE_UNSUBMIT: (id) =>
    `/api/v1/admin/organizations/${id}/tax-invoice/unsubmit`,
  ORG_EXTEND_TRIAL: (id) => `/api/v1/admin/organizations/${id}/extend-trial`,
  // Direct trial_end_date setter — accepts any date (past or future).
  // Sister of ORG_EXTEND_TRIAL. Use this when admin wants to shorten or
  // pin to a specific date rather than add N days.
  ORG_SET_TRIAL_END: (id) => `/api/v1/admin/organizations/${id}/set-trial-end`,
  ORG_CHANGE_PLAN: (id) => `/api/v1/admin/organizations/${id}/change-plan`,
  ORG_QUOTA_OVERRIDES_LIST: (id) =>
    `/api/v1/admin/organizations/${id}/quota-overrides`,
  ORG_OVERRIDE_QUOTA: (id) =>
    `/api/v1/admin/organizations/${id}/override-quota`,
  ORG_OVERRIDE_QUOTA_REVERT: (id, overrideId) =>
    `/api/v1/admin/organizations/${id}/override-quota/${overrideId}`,
  ORG_ISSUE_CREDIT: (id) => `/api/v1/admin/organizations/${id}/issue-credit`,

  // Plan Quotas Phase A — copy a plan template onto an organization.
  // Idempotent safety-first: backend skips downgrades unless caller sets
  // `force_overwrite_existing: true`.
  ORG_ASSIGN_PLAN: (id) => `/api/v1/admin/organizations/${id}/assign-plan`,

  // Phase 3 — Audit logs + impersonation
  AUDIT_LOGS: "/api/v1/admin/audit-logs",
  IMPERSONATE: (userId) => `/api/v1/admin/impersonate/${userId}`,
  IMPERSONATE_STOP: "/api/v1/admin/impersonate/stop",

  // Phase 4 — Dashboards (live backend route names)
  DASHBOARD_REVENUE_OVERVIEW: "/api/v1/admin/dashboards/revenue/overview",
  DASHBOARD_REVENUE_TOP: "/api/v1/admin/dashboards/revenue/top-organizations",
  DASHBOARD_REVENUE_PLANS: "/api/v1/admin/dashboards/revenue/plan-distribution",
  DASHBOARD_REVENUE_CHURN: "/api/v1/admin/dashboards/revenue/churn",
  DASHBOARD_AI_COST_TREND: "/api/v1/admin/dashboards/ai/cost-trend",
  DASHBOARD_AI_BY_PROVIDER: "/api/v1/admin/dashboards/ai/by-provider",
  DASHBOARD_AI_TOP_CONSUMERS: "/api/v1/admin/dashboards/ai/top-consumers",
  DASHBOARD_AI_RESPONSE_METRICS: "/api/v1/admin/dashboards/ai/response-metrics",
  DASHBOARD_OPS_FETCH_HEALTH: "/api/v1/admin/dashboards/ops/fetch-health",
  DASHBOARD_OPS_ERROR_RATE: "/api/v1/admin/dashboards/ops/error-rate",
  // Partner Integration Phase 2 — webhook delivery rollup for the Ops
  // Health dashboard tile. Window capped at 30 days server-side.
  DASHBOARD_OPS_WEBHOOK_HEALTH: "/api/v1/admin/dashboards/ops/webhook-health",
  // USAGE_TRACKING_PLAN Phase 3 — per-org per-window usage rollup for
  // billing-tier design. Sources `org_daily_stats`. Window 1-365 days.
  DASHBOARD_BILLING_BY_ORG: "/api/v1/admin/dashboards/billing/by-org",

  // Phase 5 — plans, bulk ops (live route names). Feature-flags
  // routes removed end-to-end (table dropped in migration
  // y2z3a4b5c6d7); all product capability gating lives in
  // /admin/plans/{code}/features instead.
  PLANS: "/api/v1/admin/plans",
  // Plan mutation routes key on plan_code, not id.
  PLAN_UPDATE: (planCode) => `/api/v1/admin/plans/${planCode}`,
  PLAN_ARCHIVE: (planCode) => `/api/v1/admin/plans/${planCode}/archive`,
  PLAN_RESTORE: (planCode) => `/api/v1/admin/plans/${planCode}/restore`,
  // Phase 4 capability gating — per-plan feature toggles.
  PLAN_FEATURES_GET: (planCode) =>
    `/api/v1/admin/plans/${planCode}/features`,
  PLAN_FEATURES_UPDATE: (planCode) =>
    `/api/v1/admin/plans/${planCode}/features`,
  // Free-trial defaults (super_admin only). Used by the new
  // /admin/trial-config page. GET reads the AppSettings singleton's
  // trial fields; PUT performs a partial update.
  TRIAL_CONFIG: "/api/v1/admin/trial-config",
  // /admin/case-bank/settings page (Case Law Bank Wave 1+2). GET reads
  // + PUT partial-updates the AppSettings singleton's two case-bank
  // toggle columns. Case-bank service also reads these via a separate
  // service-token endpoint at /api/v1/internal/case-bank/settings.
  CASE_BANK_CONFIG: "/api/v1/admin/case-bank-config",
  // Case Law Bank browse (Wave 4). Backend proxies to the case-bank
  // service's admin endpoints via a shared service token — the FE
  // stays same-origin.
  CASE_BANK_DOCUMENTS: "/api/v1/admin/case-bank/documents",
  CASE_BANK_DOCUMENT_DETAIL: (tid) =>
    `/api/v1/admin/case-bank/documents/${tid}`,
  CASE_BANK_DOCUMENT_BODY: (tid) =>
    `/api/v1/admin/case-bank/documents/${tid}/body`,
  CASE_BANK_DOCUMENT_COURT_COPY: (tid) =>
    `/api/v1/admin/case-bank/documents/${tid}/court-copy`,
  CASE_BANK_DOCUMENTS_ZIP: "/api/v1/admin/case-bank/documents-zip",
  CASE_BANK_TIDS_IN_BANK: "/api/v1/admin/case-bank/tids-in-bank",

  // /admin/system-config page — consolidated small operational flags
  // (selenium_mode, gst_file_download_concurrency, …). GET reads / PUT
  // partial-updates the AppSettings singleton's system-config fields.
  SYSTEM_CONFIG: "/api/v1/admin/system-config",
  // WhatsApp template library (list / CRUD / Meta sync) — dedicated
  // management pages under the admin sidebar.
  WHATSAPP_TEMPLATES: "/api/v1/admin/whatsapp-templates",

  // Celery workers + queued-task management (webhook-flood protection ops).
  // health = workers/queue depths/enqueue-flag; queued = waiting + active
  // tasks of one queue; revoke = stop one task; purge = drop a whole queue
  // (webhooks only, server-enforced).
  CELERY_HEALTH: "/api/v1/admin/celery/health",
  CELERY_QUEUED: "/api/v1/admin/celery/queued",
  CELERY_TASK_REVOKE: "/api/v1/admin/celery/tasks/revoke",
  CELERY_QUEUE_PURGE: "/api/v1/admin/celery/queues/purge",

  // AI model catalogue — drives the model-selection dropdowns. GET reads the
  // cached per-provider model list; refresh re-fetches from the providers
  // ("Fetch latest models" button); test validates one provider+model.
  AI_MODELS: "/api/v1/admin/ai-models",
  AI_MODELS_REFRESH: "/api/v1/admin/ai-models/refresh",
  AI_MODELS_TEST: "/api/v1/admin/ai-models/test",

  // Database Backup Management (super_admin only). List backups stored in S3,
  // get a short-lived presigned download URL, and trigger an on-demand backup.
  DATABASE_BACKUPS: "/api/v1/admin/database-backups",
  DATABASE_BACKUP_DOWNLOAD: (key) =>
    `/api/v1/admin/database-backups/download?key=${encodeURIComponent(key)}`,
  DATABASE_BACKUP_RUN: "/api/v1/admin/database-backups/run",

  // Platform-wide Usage (super_admin only). Mirrors the
  // /partner-admin/usage + /partner-admin/usage/timeseries shape but
  // aggregates across every organization on the platform.
  USAGE: "/api/v1/admin/usage",
  USAGE_TIMESERIES: "/api/v1/admin/usage/timeseries",

  // Lambda Analytics (super_admin only). Single-shot payload — KPIs,
  // daily trend, by-portal split, duration histogram, top + full
  // per-organization and per-partner breakdowns. The Usage page's
  // Reliability section links here via a "View More" affordance.
  LAMBDA_ANALYTICS: "/api/v1/admin/lambda-analytics",

  // Whitebooks (third-party GST API) configuration page. GET reads
  // both the config knobs + the current session/OTP-cycle status;
  // PUT saves config; the four POSTs drive the OTP / refresh / logout
  // lifecycle. All super_admin only.
  WHITEBOOKS_STATUS: "/api/v1/admin/whitebooks/status",
  // 11-Jun rework: explicit per-gst_username session list + detail.
  // Without ?gst_username= returns the full cross-GSTIN list (powers
  // the super-admin page). With ?gst_username= returns a single
  // row's status (used by the tenant card in Checkpoint 2).
  WHITEBOOKS_SESSIONS: "/api/v1/admin/whitebooks/sessions",
  WHITEBOOKS_CONFIG: "/api/v1/admin/whitebooks/config",
  WHITEBOOKS_OTP_REQUEST: "/api/v1/admin/whitebooks/otp-request",
  WHITEBOOKS_OTP_SUBMIT: "/api/v1/admin/whitebooks/otp-submit",
  WHITEBOOKS_REFRESH_NOW: "/api/v1/admin/whitebooks/refresh-now",
  WHITEBOOKS_LOGOUT: "/api/v1/admin/whitebooks/logout",

  // GST Auth Sessions log — per-GSTIN session lifecycle visibility.
  // Powers the new admin Logs page. Tokens MASKED by default; raw
  // tokens travel only through GST_AUTH_SESSION_REVEAL_TOKEN on an
  // explicit eye-icon click. Reveal calls are BE-audit-logged.
  GST_AUTH_SESSIONS: "/api/v1/admin/gst-auth-sessions",
  GST_AUTH_SESSION_DETAIL: (id) => `/api/v1/admin/gst-auth-sessions/${id}`,
  GST_AUTH_SESSION_REVEAL_TOKEN: (id) =>
    `/api/v1/admin/gst-auth-sessions/${id}/reveal-token`,

  // Cost-management config (super_admin only). Backs the
  // /admin/cost-config page. Effective-dated: every POST creates a
  // new revision row; older rows stay so historical CostEventLog
  // rows can be re-priced against whatever rate was active at their
  // moment. Per-partner overrides are sparse (NULL columns inherit
  // from the global default in effect at the same moment).
  COST_CONFIG: "/api/v1/admin/cost-config",
  COST_CONFIG_RESOLVE: "/api/v1/admin/cost-config/resolve",
  // Per-partner override endpoint — used from the Partner detail page's
  // Pricing tab (PartnerPricingTab). The bulk list endpoint was
  // removed when the per-partner override section was pulled out of
  // the central Pricing Management page.
  COST_CONFIG_PARTNER_OVERRIDE: (partnerId) =>
    `/api/v1/admin/cost-config/partner-overrides/${partnerId}`,

  // Payment History / Orders (super_admin only). Cross-tenant list +
  // detail view of every Order row. Filter chips: gateway /
  // order_type / payment_status / is_paid / organization / date
  // range; search hits transaction_id and the Razorpay order/payment
  // ids. NoticeAI does NOT offer refunds — no refund endpoint is
  // exposed here on purpose; the BE refund route exists only as an
  // engineering back-channel and must never be surfaced in any UI.
  PAYMENTS_LIST: "/api/v1/admin/payments/orders",
  PAYMENT_DETAIL: (orderId) =>
    `/api/v1/admin/payments/orders/${orderId}`,

  // Plan categories lookup table (super_admin only). Backed by the
  // `plan_categories` table seeded with `essential` / `advanced`. Shared
  // between the Plan editor, Subscriptions filter, and the dedicated
  // Plan categories CRUD page.
  PLAN_CATEGORIES: "/api/v1/admin/plan-categories",
  PLAN_CATEGORY_DETAIL: (code) => `/api/v1/admin/plan-categories/${code}`,
  PLAN_CATEGORY_ARCHIVE: (code) =>
    `/api/v1/admin/plan-categories/${code}/archive`,
  PLAN_CATEGORY_RESTORE: (code) =>
    `/api/v1/admin/plan-categories/${code}/restore`,
  BULK_INVITE_USERS: "/api/v1/admin/bulk/users/invite",
  BULK_QUOTA_BUMP: "/api/v1/admin/bulk/organizations/quota-bump",
  BULK_DEACTIVATE_USERS: "/api/v1/admin/bulk/users/deactivate",

  // Self-telemetry for the admin portal (page views, feature usage).
  // Backend endpoint lands alongside Phase 4 dashboards.
  TELEMETRY_PAGEVIEW: "/api/v1/admin/telemetry/pageview",

  // Partner Integration Phase 1 — admin surfaces for third-party integrations.
  // `PARTNERS_LIST` / `PARTNER_CREATE` / `PARTNER_UPDATE` / `PARTNER_ROTATE_SECRET`
  // / `PARTNER_DELETE` are legacy (pre-date the admin package); the
  // `_DETAIL_ENRICHED` / `_API_LOGS` / `_WEBHOOKS` / `_USAGE_SUMMARY` /
  // `_WEBHOOK_RETRY` routes are new in the admin/partner_ops.py module.
  PARTNERS_LIST: "/api/v1/admin/partners",
  PARTNER_CREATE: "/api/v1/admin/partners",
  PARTNER_DETAIL: (id) => `/api/v1/admin/partners/${id}`,
  PARTNER_UPDATE: (id) => `/api/v1/admin/partners/${id}`,
  PARTNER_DELETE: (id) => `/api/v1/admin/partners/${id}`,
  // Permanent, unlike PARTNER_DELETE which only disables. The impact route
  // reports what would be destroyed so the dialog can show it first.
  PARTNER_DELETE_IMPACT: (id) => `/api/v1/admin/partners/${id}/delete-impact`,
  PARTNER_DELETE_PERMANENT: (id) => `/api/v1/admin/partners/${id}/permanent`,
  PARTNER_ROTATE_SECRET: (id) =>
    `/api/v1/admin/partners/${id}/rotate-secret`,
  // Two credential pairs per partner (PARTNER_PORTAL_CREDITS_PLAN.md s6):
  // rotate-secret / regenerate-key take `?scope=sandbox|production`.
  PARTNER_REGENERATE_KEY: (id) => `/api/v1/admin/partners/${id}/regenerate-key`,
  PARTNER_PRODUCTION_CREDENTIALS: (id) => `/api/v1/admin/partners/${id}/production-credentials`,
  PARTNER_WEBHOOK_SECRET: (id) =>
    `/api/v1/admin/partners/${id}/webhook-secret`,
  PARTNER_WEBHOOK_SECRET_REGEN: (id) =>
    `/api/v1/admin/partners/${id}/webhook-secret/regenerate`,
  PARTNER_DETAIL_ENRICHED: (id) => `/api/v1/admin/partners/${id}/detail`,
  // Public Sandbox access requests: the queue behind the public
  // /request-access form on the partner portal. Accepting is what creates
  // the partner, its admin user, its default credits and the invite email.
  SANDBOX_ACCESS_REQUESTS: "/api/v1/admin/sandbox-access-requests",
  SANDBOX_ACCESS_REQUEST_ACCEPT: (id) =>
    `/api/v1/admin/sandbox-access-requests/${id}/accept`,
  SANDBOX_ACCESS_REQUEST_REJECT: (id) =>
    `/api/v1/admin/sandbox-access-requests/${id}/reject`,
  // Partner Sandbox credits (PARTNER_SANDBOX_DESIGN/CREDITS_PLAN.md).
  PARTNER_SANDBOX_CREDITS: (id) => `/api/v1/admin/partners/${id}/sandbox-credits`,
  PARTNER_SANDBOX_CREDITS_ADJUST: (id) =>
    `/api/v1/admin/partners/${id}/sandbox-credits/adjust`,
  PARTNER_SANDBOX_CREDITS_RESET: (id) =>
    `/api/v1/admin/partners/${id}/sandbox-credits/reset`,
  PARTNER_SANDBOX_CREDITS_LEDGER: (id) =>
    `/api/v1/admin/partners/${id}/sandbox-credits/ledger`,
  // Partner Direct API credits (PARTNER_PRODUCTION_CREDITS_PLAN.md v2): the
  // paid pool, bound to a subscription period.
  PARTNER_CREDITS: (id) => `/api/v1/admin/partners/${id}/credits`,
  PARTNER_CREDITS_ADJUST: (id) => `/api/v1/admin/partners/${id}/credits/adjust`,
  PARTNER_CREDITS_LEDGER: (id) => `/api/v1/admin/partners/${id}/credits/ledger`,
  PARTNER_SUBSCRIPTIONS: (id) => `/api/v1/admin/partners/${id}/subscriptions`,
  PARTNER_SUBSCRIPTION: (id, sid) => `/api/v1/admin/partners/${id}/subscriptions/${sid}`,
  PARTNER_SUBSCRIPTION_CANCEL: (id, sid) =>
    `/api/v1/admin/partners/${id}/subscriptions/${sid}/cancel`,
  PARTNER_API_LOGS: (id) => `/api/v1/admin/partners/${id}/api-logs`,
  PARTNER_WEBHOOKS: (id) => `/api/v1/admin/partners/${id}/webhooks`,
  PARTNER_USAGE_SUMMARY: (id) =>
    `/api/v1/admin/partners/${id}/usage-summary`,
  // Phase 3.3 — bulk synchronous webhook redelivery (≤500 rows per call).
  PARTNER_WEBHOOK_BULK_RETRY: (id) =>
    `/api/v1/admin/partners/${id}/webhooks/bulk-retry`,
  PARTNER_WEBHOOK_RETRY: (id, logId) =>
    `/api/v1/admin/partners/${id}/webhooks/${logId}/retry`,

  // Module 10 Phase 1 — partner_admin staff bootstrap. Super_admin only.
  // POST creates the first partner_admin staff user (or subsequent ones up
  // to partner.max_admin_users); GET lists existing partner_admins for
  // triage. Same path, different verbs.
  PARTNER_ADMIN_USER_DELETE: (partnerId, userId) =>
    `/api/v1/admin/partners/${partnerId}/admin-users/${userId}`,
  PARTNER_ADMIN_USERS: (id) =>
    `/api/v1/admin/partners/${id}/admin-users`,

  // Cross-platform service-table counts (GST / IT / TDS-TCS) for the
  // admin sidebar badges. Single endpoint returns 8 counts in one call.
  SERVICE_COUNTS: "/api/v1/admin/service-counts",

  // Service-table paginated list endpoints (super_admin only). Each
  // returns {items, total, page, page_size, total_pages} with org name
  // + assigned user name pre-joined so the UI renders flat columns.
  // Search is a single text param applied to a per-endpoint set of
  // ILIKE-able columns (client_name / ref_id / company_name etc.).
  SERVICE_GST_CLIENTS: "/api/v1/admin/gst/clients",
  // Delete ALL notices (+ S3 attachments) for one client, keeping the client.
  CLIENT_DELETE_ALL_NOTICES: (portal, clientId) =>
    `/api/v1/admin/${portal}/clients/${clientId}/notices/delete-all`,
  SERVICE_GST_NOTICES: "/api/v1/admin/gst/notices",
  SERVICE_GST_ADDITIONAL_NOTICES: "/api/v1/admin/gst/additional-notices",
  SERVICE_IT_CLIENTS: "/api/v1/admin/income-tax/clients",
  SERVICE_IT_EPROCEEDINGS: "/api/v1/admin/income-tax/eproceedings",
  SERVICE_IT_ROD: "/api/v1/admin/income-tax/response-outstanding-demands",
  SERVICE_TDS_CLIENTS: "/api/v1/admin/tds-tcs/clients",
  SERVICE_TDS_NOTICES: "/api/v1/admin/tds-tcs/notices",

  // Distinct partner_client_ref strings — feeds the AutoComplete
  // suggestions on each respective Clients filter bar. Capped at 500
  // distinct values server-side per table.
  SERVICE_GST_CLIENT_PARTNER_REFS:
    "/api/v1/admin/gst/clients/partner-refs",
  SERVICE_IT_CLIENT_PARTNER_REFS:
    "/api/v1/admin/income-tax/clients/partner-refs",
  SERVICE_TDS_CLIENT_PARTNER_REFS:
    "/api/v1/admin/tds-tcs/clients/partner-refs",

  // Assignees lookup — users who actually have at least one client
  // assigned in the given table. Drives the "Assigned User" filter
  // dropdown so it doesn't list all platform users.
  SERVICE_GST_CLIENT_ASSIGNEES:
    "/api/v1/admin/gst/clients/assignees",
  SERVICE_IT_CLIENT_ASSIGNEES:
    "/api/v1/admin/income-tax/clients/assignees",
  SERVICE_TDS_CLIENT_ASSIGNEES:
    "/api/v1/admin/tds-tcs/clients/assignees",

  // Distinct-value lookups for the GST Notices filter bar
  // (AutoComplete suggestion sources). Capped at 500 distinct values
  // server-side per column.
  SERVICE_GST_NOTICES_CLIENT_NAMES:
    "/api/v1/admin/gst/notices/client-names",
  SERVICE_GST_NOTICES_TYPES:
    "/api/v1/admin/gst/notices/types",
  SERVICE_GST_NOTICES_RESPONSE_STATUSES:
    "/api/v1/admin/gst/notices/response-statuses",

  // Distinct-value lookups for the GST Additional Notices filter bar.
  SERVICE_GST_ADDITIONAL_NOTICES_CLIENT_NAMES:
    "/api/v1/admin/gst/additional-notices/client-names",
  SERVICE_GST_ADDITIONAL_NOTICES_TYPES:
    "/api/v1/admin/gst/additional-notices/types",
  SERVICE_GST_ADDITIONAL_NOTICES_RESPONSE_STATUSES:
    "/api/v1/admin/gst/additional-notices/response-statuses",

  // Distinct-value lookups for the IT E-Proceedings filter bar.
  // `types` returns distinct proceeding_type values.
  SERVICE_IT_EPROCEEDINGS_CLIENT_NAMES:
    "/api/v1/admin/income-tax/eproceedings/client-names",
  SERVICE_IT_EPROCEEDINGS_TYPES:
    "/api/v1/admin/income-tax/eproceedings/types",
  SERVICE_IT_EPROCEEDINGS_RESPONSE_STATUSES:
    "/api/v1/admin/income-tax/eproceedings/response-statuses",

  // Distinct-value lookups for the IT Response-to-Outstanding-Demands
  // filter bar. `types` returns distinct response_type values.
  SERVICE_IT_ROD_CLIENT_NAMES:
    "/api/v1/admin/income-tax/response-outstanding-demands/client-names",
  SERVICE_IT_ROD_TYPES:
    "/api/v1/admin/income-tax/response-outstanding-demands/types",
  SERVICE_IT_ROD_RESPONSE_STATUSES:
    "/api/v1/admin/income-tax/response-outstanding-demands/response-statuses",

  // Distinct-value lookups for the TDS Notices filter bar.
  SERVICE_TDS_NOTICES_CLIENT_NAMES:
    "/api/v1/admin/tds-tcs/notices/client-names",
  SERVICE_TDS_NOTICES_FINANCIAL_YEARS:
    "/api/v1/admin/tds-tcs/notices/financial-years",

  // R19 — parallel_fetch_logs admin surface. Paginated list + hard
  // delete (single + bulk) + distinct lookups for the Status / Portal
  // Type Select filters.
  PARALLEL_FETCH_LOGS: "/api/v1/admin/parallel-fetch-logs",
  PARALLEL_FETCH_LOGS_DELETE: (id) =>
    `/api/v1/admin/parallel-fetch-logs/${id}`,
  PARALLEL_FETCH_LOGS_BULK_DELETE:
    "/api/v1/admin/parallel-fetch-logs/bulk-delete",
  PARALLEL_FETCH_LOGS_PORTAL_TYPES:
    "/api/v1/admin/parallel-fetch-logs/portal-types",
  PARALLEL_FETCH_LOGS_STATUSES:
    "/api/v1/admin/parallel-fetch-logs/statuses",
  // R20 — narrowed Select lookups (only the users/orgs that actually
  // appear in `parallel_fetch_logs`), plus the click-through detail
  // endpoint feeding the new `/admin/parallel-fetch-logs/:logId` page.
  PARALLEL_FETCH_LOGS_USERS:
    "/api/v1/admin/parallel-fetch-logs/users",
  PARALLEL_FETCH_LOGS_ORGANIZATIONS:
    "/api/v1/admin/parallel-fetch-logs/organizations",
  PARALLEL_FETCH_LOGS_DETAIL: (id) =>
    `/api/v1/admin/parallel-fetch-logs/${id}`,

  // R22 — cost_event_logs admin surface. List + detail + 4 narrowed
  // lookups (users / organizations / categories / event-types) +
  // single+bulk hard delete. Same envelope as parallel_fetch_logs.
  COST_EVENT_LOGS: "/api/v1/admin/cost-event-logs",
  COST_EVENT_LOGS_DETAIL: (id) => `/api/v1/admin/cost-event-logs/${id}`,
  COST_EVENT_LOGS_USERS: "/api/v1/admin/cost-event-logs/users",
  COST_EVENT_LOGS_ORGANIZATIONS:
    "/api/v1/admin/cost-event-logs/organizations",
  COST_EVENT_LOGS_CATEGORIES:
    "/api/v1/admin/cost-event-logs/categories",
  COST_EVENT_LOGS_EVENT_TYPES:
    "/api/v1/admin/cost-event-logs/event-types",

  // Partner billing (Phase 2) — partner-facing INR billing rows + on-demand
  // monthly bill (super_admin).
  PARTNER_BILLING_LOGS: "/api/v1/admin/partner-billing-logs",
  PARTNER_BILLING_LOGS_CATEGORIES:
    "/api/v1/admin/partner-billing-logs/categories",
  PARTNER_BILLING_LOGS_DETAIL: (id) =>
    `/api/v1/admin/partner-billing-logs/${id}`,
  PARTNER_BILLING_SUMMARY: (partnerId) =>
    `/api/v1/admin/partners/${partnerId}/billing-summary`,

  // R26 — OTP admin surface. List + detail + single+bulk hard
  // delete. No narrowed lookups (OTP table has no FK joins).
  OTPS: "/api/v1/admin/otps",
  OTPS_DETAIL: (id) => `/api/v1/admin/otps/${id}`,

  // Email logs admin surface — mirrors OTPS shape. List has lightweight
  // serializer (drops body/user_agent); detail has full body + context.
  EMAIL_LOGS: "/api/v1/admin/email-logs",
  EMAIL_LOGS_DETAIL: (id) => `/api/v1/admin/email-logs/${id}`,

  // R27 — generate_response_logs admin surface. List + detail + 2
  // narrowed lookups (doctypes / organizations) + single+bulk hard
  // delete. Same envelope as parallel_fetch_logs / cost_event_logs.
  GENERATE_RESPONSE_LOGS: "/api/v1/admin/generate-response-logs",
  GENERATE_RESPONSE_LOGS_DETAIL: (id) =>
    `/api/v1/admin/generate-response-logs/${id}`,
  GENERATE_RESPONSE_LOGS_DOCTYPES:
    "/api/v1/admin/generate-response-logs/doctypes",
  GENERATE_RESPONSE_LOGS_ORGANIZATIONS:
    "/api/v1/admin/generate-response-logs/organizations",

  // Import Log — admin surface for `data_imports` (Excel bulk-import
  // history). Mirrors GENERATE_RESPONSE_LOGS shape + adds DOWNLOAD_FILE
  // for the original Excel via presigned S3 URL.
  IMPORT_LOGS: "/api/v1/admin/data-imports",
  IMPORT_LOGS_DETAIL: (id) => `/api/v1/admin/data-imports/${id}`,
  IMPORT_LOGS_DOCTYPES: "/api/v1/admin/data-imports/doctypes",
  IMPORT_LOGS_ORGANIZATIONS: "/api/v1/admin/data-imports/organizations",
  IMPORT_LOGS_DOWNLOAD_FILE: (id) =>
    `/api/v1/admin/data-imports/${id}/download-file`,

  // Login Failed Log — admin surface for `login_failed` (portal login
  // failure tracking). Mirrors IMPORT_LOGS shape.
  LOGIN_FAILED_LOGS: "/api/v1/admin/login-failed-logs",
  LOGIN_FAILED_LOGS_DETAIL: (id) => `/api/v1/admin/login-failed-logs/${id}`,
  LOGIN_FAILED_LOGS_PORTALS: "/api/v1/admin/login-failed-logs/portals",
  LOGIN_FAILED_LOGS_FETCH_TYPES: "/api/v1/admin/login-failed-logs/fetch-types",
  LOGIN_FAILED_LOGS_ORGANIZATIONS:
    "/api/v1/admin/login-failed-logs/organizations",

  // R29 — comprehensive_analysis_query_logs admin surface. List +
  // detail + 3 narrowed lookups (voucher-types / client-types /
  // organizations) + single+bulk hard delete. Same envelope.
  COMP_ANALYSIS_QUERY_LOGS:
    "/api/v1/admin/comprehensive-analysis-query-logs",
  COMP_ANALYSIS_QUERY_LOGS_DETAIL: (id) =>
    `/api/v1/admin/comprehensive-analysis-query-logs/${id}`,
  COMP_ANALYSIS_QUERY_LOGS_VOUCHER_TYPES:
    "/api/v1/admin/comprehensive-analysis-query-logs/voucher-types",
  COMP_ANALYSIS_QUERY_LOGS_CLIENT_TYPES:
    "/api/v1/admin/comprehensive-analysis-query-logs/client-types",
  COMP_ANALYSIS_QUERY_LOGS_ORGANIZATIONS:
    "/api/v1/admin/comprehensive-analysis-query-logs/organizations",
};

// ---------------------------------------------------------------------------
// Module 10 — Partner Admin Portal endpoints. Mounted under /partner-admin
// (note hyphen — distinguishes from the m2m /partner namespace). Caller is
// a partner_admin user (role='partner_admin' + partner_id claim); BE
// auto-scopes to caller's partner_id, so no path/query parameters carry it.
// ---------------------------------------------------------------------------
export const PARTNER_ADMIN = {
  // Phase 2 — Organizations CRUD
  ORGS: "/api/v1/partner-admin/organizations",
  ORG_CREATE: "/api/v1/partner-admin/organizations",
  ORG_DETAIL: (id) => `/api/v1/partner-admin/organizations/${id}`,
  ORG_UPDATE: (id) => `/api/v1/partner-admin/organizations/${id}`,
  ORG_SOFT_DELETE: (id) =>
    `/api/v1/partner-admin/organizations/${id}/soft-delete`,
  ORG_RESTORE: (id) => `/api/v1/partner-admin/organizations/${id}/restore`,
  // Reversible "disable" — pauses all login access for the org's
  // users; data is preserved. Distinct from soft-delete (stronger,
  // lifecycle-different). Both endpoints partner-scoped server-side.
  ORG_SUSPEND: (id) => `/api/v1/partner-admin/organizations/${id}/suspend`,
  ORG_UNSUSPEND: (id) => `/api/v1/partner-admin/organizations/${id}/unsuspend`,

  // Phase 3 — Tenant users (inside the partner's orgs)
  USERS: "/api/v1/partner-admin/users",
  USER_CREATE: "/api/v1/partner-admin/users",
  USER_DETAIL: (id) => `/api/v1/partner-admin/users/${id}`,
  USER_UPDATE: (id) => `/api/v1/partner-admin/users/${id}`,
  USER_SOFT_DELETE: (id) =>
    `/api/v1/partner-admin/users/${id}/soft-delete`,
  USER_RESTORE: (id) => `/api/v1/partner-admin/users/${id}/restore`,
  // Password ops — partner-admin acts on tenant users in their orgs.
  // SET = direct admin-typed password (support call workflow).
  // FORCE_RESET = email a 1h reset link to the user. Both audit-logged
  // server-side; never log the plaintext password client-side.
  USER_SET_PASSWORD: (id) =>
    `/api/v1/partner-admin/users/${id}/set-password`,
  USER_FORCE_RESET: (id) =>
    `/api/v1/partner-admin/users/${id}/force-password-reset`,
  // Direct activate — bypasses the invite-email loop. Partner-admin
  // mirror of the super-admin /admin/users/{id}/activate endpoint
  // (shipped 2026-05-06). Used when an invited user can't access the
  // email link and the partner admin wants to mark them ready-to-login.
  // Flag-flip only — pair with USER_SET_PASSWORD or USER_FORCE_RESET
  // if a credential change is also required.
  USER_ACTIVATE: (id) =>
    `/api/v1/partner-admin/users/${id}/activate`,

  // Phase 3 — Peer partner_admin staff. NOTE: no restore endpoint —
  // once a peer is disabled, only super_admin can re-enable them via
  // /admin/users/{id}/enable. UI surfaces this in copy.
  ADMIN_USERS: "/api/v1/partner-admin/admin-users",
  ADMIN_USER_CREATE: "/api/v1/partner-admin/admin-users",
  ADMIN_USER_UPDATE: (id) => `/api/v1/partner-admin/admin-users/${id}`,
  ADMIN_USER_SOFT_DELETE: (id) =>
    `/api/v1/partner-admin/admin-users/${id}/soft-delete`,

  // Phase 3 — Read-only views
  PROFILE: "/api/v1/partner-admin/profile",
  USAGE: "/api/v1/partner-admin/usage",
  USAGE_TIMESERIES: "/api/v1/partner-admin/usage/timeseries",
  // On-demand monthly bill for the calling partner — same
  // `compute_monthly_bill` engine the super-admin Partner Detail →
  // Billing Overview tab uses, so the numbers match byte-for-byte.
  // Partner-scoped server-side via the caller's partner_id.
  BILLING_SUMMARY: "/api/v1/partner-admin/billing-summary",
  // Direct API credits for the calling partner (read-only; the current
  // subscription period, balances and history). PARTNER_PRODUCTION_CREDITS_PLAN.md.
  CREDITS: "/api/v1/partner-admin/credits",
  CREDITS_LEDGER: "/api/v1/partner-admin/credits/ledger",
  // Self-service API credentials (sandbox + production pairs).
  // Direct API home numbers, API Usage & History, profile edits (PARTNER_PORTAL_CREDITS_PLAN.md).
  DASHBOARD: "/api/v1/partner-admin/dashboard",
  API_HISTORY: "/api/v1/partner-admin/api-history",
  API_HISTORY_EXPORT: "/api/v1/partner-admin/api-history/export",
  PROFILE_UPDATE: "/api/v1/partner-admin/profile",
  PROFILE_IMAGE: "/api/v1/partner-admin/profile/image",
  CREDENTIALS: "/api/v1/partner-admin/credentials",
  // Endpoint reference, already filtered server-side by this partner's tax portals.
  API_DOCS: "/api/v1/partner-admin/api-docs",
  // First issue of the production pair, self-service (shown once).
  CREDENTIALS_ISSUE: "/api/v1/partner-admin/credentials/production/issue",
  CREDENTIALS_ROTATE: (scope) => `/api/v1/partner-admin/credentials/${scope}/rotate-secret`,
  CREDENTIALS_REGENERATE: (scope) => `/api/v1/partner-admin/credentials/${scope}/regenerate`,
  // Resolved cost-config rates for the calling partner (partner-
  // override layered on global default). Read-only — super admin
  // manages the underlying values from /admin/cost-config.
  COST_CONFIG_RESOLVED: "/api/v1/partner-admin/cost-config/resolved",
  // Aggregated total of `Per Notice Fetch` cost-event-log rows for
  // one partner-onboarded org over a date range. Powers the per-org
  // Per Notice Fetch costing card on the Org-detail Usage tab.
  PER_NOTICE_FETCH_COST: (orgId) =>
    `/api/v1/partner-admin/organizations/${orgId}/per-notice-fetch-cost`,
  WEBHOOKS: "/api/v1/partner-admin/webhooks",
  AUDIT_LOGS: "/api/v1/partner-admin/audit-logs",

  // NoticeAI — Manage Clients (mirrors the super_admin SERVICE_*
  // endpoints, but auto-scoped to caller's partner_id. Response shape
  // is byte-for-byte identical so the shared AdminClientsPage component
  // is reused unchanged.)
  SERVICE_GST_CLIENTS: "/api/v1/partner-admin/gst/clients",
  SERVICE_IT_CLIENTS: "/api/v1/partner-admin/income-tax/clients",
  SERVICE_TDS_CLIENTS: "/api/v1/partner-admin/tds-tcs/clients",
  // Distinct partner_client_ref values — feeds the AutoComplete on the
  // Clients filter bar. Scoped to caller's partner.
  SERVICE_GST_CLIENT_PARTNER_REFS:
    "/api/v1/partner-admin/gst/client-partner-refs",
  SERVICE_IT_CLIENT_PARTNER_REFS:
    "/api/v1/partner-admin/income-tax/client-partner-refs",
  SERVICE_TDS_CLIENT_PARTNER_REFS:
    "/api/v1/partner-admin/tds-tcs/client-partner-refs",
  // Assignees lookup — users assigned to ≥1 client under caller's
  // partner. Drives the "Assigned User" Select on each filter bar.
  SERVICE_GST_CLIENT_ASSIGNEES:
    "/api/v1/partner-admin/gst/client-assignees",
  SERVICE_IT_CLIENT_ASSIGNEES:
    "/api/v1/partner-admin/income-tax/client-assignees",
  SERVICE_TDS_CLIENT_ASSIGNEES:
    "/api/v1/partner-admin/tds-tcs/client-assignees",
  // Single-client detail (partner-scoped — 404s for cross-partner client
  // ids; credential reveal deliberately omitted vs. the super-admin
  // variant). URL builders so the FE detail route can pass `(id) => ...`.
  SERVICE_GST_CLIENT_DETAIL: (id) =>
    `/api/v1/partner-admin/gst/clients/${id}`,
  SERVICE_IT_CLIENT_DETAIL: (id) =>
    `/api/v1/partner-admin/income-tax/clients/${id}`,
  SERVICE_TDS_CLIENT_DETAIL: (id) =>
    `/api/v1/partner-admin/tds-tcs/clients/${id}`,

  // ── Notice lists (partner-scoped — same response shape as the
  // super-admin variants so the shared FE list components are reused
  // unchanged).
  SERVICE_GST_NOTICES: "/api/v1/partner-admin/gst/notices",
  SERVICE_GST_ADDITIONAL_NOTICES:
    "/api/v1/partner-admin/gst/additional-notices",
  SERVICE_IT_EPROCEEDINGS:
    "/api/v1/partner-admin/income-tax/eproceedings",
  SERVICE_IT_ROD:
    "/api/v1/partner-admin/income-tax/response-outstanding-demands",
  SERVICE_TDS_NOTICES: "/api/v1/partner-admin/tds-tcs/notices",

  // Distinct-value lookups per notice list — drive the filter bar
  // AutoComplete + Select dropdowns. TDS has no types / response-statuses
  // lookups, matching the super-admin shape exactly.
  SERVICE_GST_NOTICES_CLIENT_NAMES:
    "/api/v1/partner-admin/gst/notices/client-names",
  SERVICE_GST_NOTICES_TYPES:
    "/api/v1/partner-admin/gst/notices/types",
  SERVICE_GST_NOTICES_RESPONSE_STATUSES:
    "/api/v1/partner-admin/gst/notices/response-statuses",
  SERVICE_GST_ADDITIONAL_NOTICES_CLIENT_NAMES:
    "/api/v1/partner-admin/gst/additional-notices/client-names",
  SERVICE_GST_ADDITIONAL_NOTICES_TYPES:
    "/api/v1/partner-admin/gst/additional-notices/types",
  SERVICE_GST_ADDITIONAL_NOTICES_RESPONSE_STATUSES:
    "/api/v1/partner-admin/gst/additional-notices/response-statuses",
  SERVICE_IT_EPROCEEDINGS_CLIENT_NAMES:
    "/api/v1/partner-admin/income-tax/eproceedings/client-names",
  SERVICE_IT_EPROCEEDINGS_TYPES:
    "/api/v1/partner-admin/income-tax/eproceedings/types",
  SERVICE_IT_EPROCEEDINGS_RESPONSE_STATUSES:
    "/api/v1/partner-admin/income-tax/eproceedings/response-statuses",
  SERVICE_IT_ROD_CLIENT_NAMES:
    "/api/v1/partner-admin/income-tax/response-outstanding-demands/client-names",
  SERVICE_IT_ROD_TYPES:
    "/api/v1/partner-admin/income-tax/response-outstanding-demands/types",
  SERVICE_IT_ROD_RESPONSE_STATUSES:
    "/api/v1/partner-admin/income-tax/response-outstanding-demands/response-statuses",
  SERVICE_TDS_NOTICES_CLIENT_NAMES:
    "/api/v1/partner-admin/tds-tcs/notices/client-names",
  SERVICE_TDS_NOTICES_FINANCIAL_YEARS:
    "/api/v1/partner-admin/tds-tcs/notices/financial-years",

  // Single-notice detail — one generic endpoint per the BE dispatch
  // /{portal}/{notice_path}/{notice_id}. Each FE route passes its own
  // (portal, notice_path) so the same URL builder family stays compact.
  SERVICE_GST_NOTICE_DETAIL: (id) =>
    `/api/v1/partner-admin/gst/notices/${id}`,
  SERVICE_GST_ADDITIONAL_NOTICE_DETAIL: (id) =>
    `/api/v1/partner-admin/gst/additional-notices/${id}`,
  SERVICE_IT_EPROCEEDING_DETAIL: (id) =>
    `/api/v1/partner-admin/income-tax/eproceedings/${id}`,
  SERVICE_IT_ROD_DETAIL: (id) =>
    `/api/v1/partner-admin/income-tax/response-outstanding-demands/${id}`,
  SERVICE_TDS_NOTICE_DETAIL: (id) =>
    `/api/v1/partner-admin/tds-tcs/notices/${id}`,
};

// ---------------------------------------------------------------------------
// Module 11 — Iframe + Embed Surface. Public-ish routes designed to be
// rendered inside a partner's iframe (e.g. ZOHO embedding NoticeAI). Auth
// uses partner-SSO JWTs minted via /partner/auth/token; the iframe receives
// the JWT in `?token=` on first load (per spec §3.2). Branding is served
// without auth so the iframe shell can paint before the JWT is decoded.
// ---------------------------------------------------------------------------
export const EMBED = {
  // Phase 1 BE health check. Returns {status, partner_id} when called
  // with a valid SSO JWT. FE uses it to confirm the iframe security
  // handshake before rendering tenant content.
  //
  // NOTE: `/embed/*` mounts at the SERVER ROOT, not under `/api/v1/`,
  // so the iframe-security middleware can scope its CSP override on
  // the literal `/embed/` prefix (see backend/app/main.py:165 +
  // app/api/v1/embed.py:1). This is on purpose — every other admin /
  // partner / tenant route stays under /api/v1/ with the existing
  // X-Frame-Options: DENY header.
  PING: "/embed/ping",
  // Public partner branding (no auth). Caller passes the partner_id
  // pulled from the embed JWT's claims.
  BRANDING: (partnerId) => `/api/v1/branding/${partnerId}`,
};

// ─── GST Appeals (APL-01 first-appeal drafting) ─────────────────────────────
export const APPEALS = {
  LIST: "/api/v1/gst/appeals",
  APPEALABLE: "/api/v1/gst/appeals/appealable",
  CASE_DOCUMENTS: (caseId) => `/api/v1/gst/appeals/case/${caseId}/documents`,
  CREATE_DRAFT: "/api/v1/gst/appeals/draft",
  APPROVE_ISSUES: (draftId) => `/api/v1/gst/appeals/${draftId}/issues/approve`,
  GET: (draftId) => `/api/v1/gst/appeals/${draftId}`,
  UPDATE: (draftId) => `/api/v1/gst/appeals/${draftId}`,
  DOCX: (draftId, doc) => `/api/v1/gst/appeals/${draftId}/docx?doc=${doc}`,
  APL01: (draftId) => `/api/v1/gst/appeals/${draftId}/apl01`,
  MANUAL_ORDER: "/api/v1/gst/appeals/manual-order",
  // Persisted supporting-doc uploads (survive refresh — 11-07-2026)
  SUPPORTING_DOCS: (caseId) =>
    `/api/v1/gst/appeals/case/${caseId}/supporting-docs`,
  SUPPORTING_DOC: (attachmentId) =>
    `/api/v1/gst/appeals/supporting-docs/${attachmentId}`,
};

// ─── Income Tax Appeals (Form 35 first appeal — E-Proceeding entry) ─────────
export const IT_APPEALS = {
  LIST: "/api/v1/income-tax/appeals",
  PROCEEDING_DOCUMENTS: (epId) =>
    `/api/v1/income-tax/appeals/proceeding/${epId}/documents`,
  CREATE_DRAFT: "/api/v1/income-tax/appeals/draft",
  FORM35_DATA: (draftId) => `/api/v1/income-tax/appeals/${draftId}/form35-data`,
  APPROVE_ISSUES: (draftId) =>
    `/api/v1/income-tax/appeals/${draftId}/issues/approve`,
  GET: (draftId) => `/api/v1/income-tax/appeals/${draftId}`,
  UPDATE: (draftId) => `/api/v1/income-tax/appeals/${draftId}`,
  FORM35: (draftId) => `/api/v1/income-tax/appeals/${draftId}/form35`,
  // Persisted supporting-doc uploads (survive refresh — 11-07-2026)
  SUPPORTING_DOCS: (epId) =>
    `/api/v1/income-tax/appeals/proceeding/${epId}/supporting-docs`,
  SUPPORTING_DOC: (attachmentId) =>
    `/api/v1/income-tax/appeals/supporting-docs/${attachmentId}`,
};
